/**
 * Lazy loading with automatic retry and page reload on chunk load failure.
 *
 * Handles the common issue where after a new deployment, browsers may try to load
 * old chunk files that no longer exist on the server.
 *
 * Uses sessionStorage to track reload attempts and prevent infinite reload loops.
 */

import { lazy, ComponentType } from 'react';

// Storage key prefix for tracking reload attempts
const RELOAD_KEY_PREFIX = 'chunk_reload_';

// Max time to consider a reload attempt as "recent" (5 seconds)
const RELOAD_TIMEOUT_MS = 5000;

/**
 * Check if a chunk load error
 */
function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('loading chunk') ||
      message.includes('loading css chunk') ||
      message.includes('unexpected token') || // Often happens with HTML error pages
      message.includes('syntax error') // When server returns non-JS content
    );
  }
  return false;
}

/**
 * Get storage key for a specific import path
 */
function getStorageKey(importPath: string): string {
  // Create a simple hash of the import path
  const hash = importPath.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);
  return `${RELOAD_KEY_PREFIX}${Math.abs(hash)}`;
}

/**
 * Check if we recently tried to reload for this chunk
 */
function hasRecentlyReloaded(key: string): boolean {
  try {
    const timestamp = sessionStorage.getItem(key);
    if (!timestamp) return false;

    const reloadTime = parseInt(timestamp, 10);
    const now = Date.now();

    // If reload was within timeout, consider it recent
    if (now - reloadTime < RELOAD_TIMEOUT_MS) {
      return true;
    }

    // Clean up old entry
    sessionStorage.removeItem(key);
    return false;
  } catch {
    // sessionStorage not available
    return false;
  }
}

/**
 * Mark that we're about to reload for this chunk
 */
function markReloadAttempt(key: string): void {
  try {
    sessionStorage.setItem(key, Date.now().toString());
  } catch {
    // sessionStorage not available, proceed with reload anyway
  }
}

/**
 * Wrapper for React.lazy that handles chunk loading failures by reloading the page.
 *
 * @param importFn - Dynamic import function, e.g. () => import('./MyComponent')
 * @param importPath - Optional path identifier for tracking (defaults to function string)
 * @returns Lazy component that auto-reloads on chunk errors
 *
 * @example
 * ```tsx
 * const MyPage = lazyWithRetry(() => import('./pages/MyPage'));
 * ```
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
  importPath?: string
): React.LazyExoticComponent<T> {
  // Use the function string as a fallback identifier
  const path = importPath || importFn.toString();
  const storageKey = getStorageKey(path);

  return lazy(async () => {
    try {
      // Attempt to load the module
      return await importFn();
    } catch (error) {
      // Check if this is a chunk loading error
      if (isChunkLoadError(error)) {
        // Check if we already tried reloading recently
        if (!hasRecentlyReloaded(storageKey)) {
          // Mark this reload attempt
          markReloadAttempt(storageKey);

          // Force a full page reload to get fresh chunk manifest
          window.location.reload();

          // Return a never-resolving promise to prevent rendering while reloading
          return new Promise(() => {});
        }

        // We already tried reloading, throw error to show error boundary
        console.error('[lazyWithRetry] Chunk load failed after reload attempt:', error);
      }

      // Re-throw for other errors or after failed reload
      throw error;
    }
  });
}

export default lazyWithRetry;
